import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Edit2, Trash2, Trophy, Zap, Target, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function AdminChallengesPage() {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState({ default_templates: [], custom_templates: [] });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [aiTheme, setAiTheme] = useState('');
  const [aiDifficulty, setAiDifficulty] = useState('medium');
  const [formData, setFormData] = useState({
    title_fr: '',
    title_en: '',
    title_pt: '',
    description_fr: '',
    description_en: '',
    description_pt: '',
    xp_reward: 10,
    challenge_type: 'custom',
    target: '',
    languages: ['fr', 'en', 'pt']
  });

  useEffect(() => {
    if (!user?.admin_role) {
      navigate('/dashboard');
      return;
    }
    fetchTemplates();
  }, [user, navigate]);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/challenges/admin/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Erreur lors du chargement des défis');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const payload = {
        title_fr: formData.title_fr,
        title_en: formData.title_en,
        title_pt: formData.title_pt,
        description_fr: formData.description_fr,
        description_en: formData.description_en,
        description_pt: formData.description_pt,
        xp_reward: parseInt(formData.xp_reward),
        challenge_type: formData.challenge_type,
        target: formData.target ? parseInt(formData.target) : null,
        languages: formData.languages.join(',')
      };
      
      await api.post('/challenges/admin/templates', null, { params: payload });
      toast.success('Défi créé avec succès');
      setShowCreateModal(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdate = async () => {
    try {
      const payload = {
        title_fr: formData.title_fr,
        title_en: formData.title_en,
        title_pt: formData.title_pt,
        description_fr: formData.description_fr,
        description_en: formData.description_en,
        description_pt: formData.description_pt,
        xp_reward: parseInt(formData.xp_reward),
        languages: formData.languages.join(',')
      };
      
      await api.put(`/challenges/admin/templates/${editingTemplate.id}`, null, { params: payload });
      toast.success('Défi mis à jour');
      setEditingTemplate(null);
      resetForm();
      fetchTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleGenerateAI = async () => {
    setGeneratingAI(true);
    try {
      const response = await api.post('/challenges/admin/generate-ai', {
        theme: aiTheme || null,
        difficulty: aiDifficulty,
        language: language
      });
      
      if (response.data.success) {
        const generated = response.data.generated_challenge;
        setFormData({
          title_fr: generated.title_fr || '',
          title_en: generated.title_en || '',
          title_pt: generated.title_pt || '',
          description_fr: generated.description_fr || '',
          description_en: generated.description_en || '',
          description_pt: generated.description_pt || '',
          xp_reward: generated.xp_reward || 15,
          challenge_type: generated.type || 'custom',
          target: generated.target || '',
          languages: generated.languages || ['fr', 'en', 'pt']
        });
        toast.success('Défi généré par IA !');
      }
    } catch (error) {
      console.error('Error generating with AI:', error);
      toast.error('Erreur lors de la génération IA');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce défi ?')) return;
    
    try {
      await api.delete(`/challenges/admin/templates/${templateId}`);
      toast.success('Défi supprimé');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await api.put(`/challenges/admin/templates/${template.id}`, null, { 
        params: { active: !template.active } 
      });
      fetchTemplates();
    } catch (error) {
      console.error('Error toggling template:', error);
      toast.error('Erreur');
    }
  };

  const resetForm = () => {
    setFormData({
      title_fr: '',
      title_en: '',
      title_pt: '',
      description_fr: '',
      description_en: '',
      description_pt: '',
      xp_reward: 10,
      challenge_type: 'custom',
      target: '',
      languages: ['fr', 'en', 'pt']
    });
    setAiTheme('');
    setAiDifficulty('medium');
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setFormData({
      title_fr: template.title_fr || '',
      title_en: template.title_en || '',
      title_pt: template.title_pt || '',
      description_fr: template.description_fr || '',
      description_en: template.description_en || '',
      description_pt: template.description_pt || '',
      xp_reward: template.xp_reward || 10,
      challenge_type: template.type || 'custom',
      target: template.target || '',
      languages: template.languages || ['fr', 'en', 'pt']
    });
  };

  const toggleLanguage = (lang) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang]
    }));
  };

  const challengeTypes = [
    { value: 'custom', label: 'Personnalisé' },
    { value: 'no_spend', label: 'Pas de dépense' },
    { value: 'track_count', label: 'Nombre de transactions' },
    { value: 'spend_limit', label: 'Limite de dépense' },
    { value: 'different_categories', label: 'Catégories différentes' },
    { value: 'ocr_scan', label: 'Scanner un ticket' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 bg-background/80 backdrop-blur border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Trophy className="w-6 h-6 text-yellow-500" />
                {language === 'fr' ? 'Gestion des Défis' : language === 'pt' ? 'Gestão de Desafios' : 'Challenge Management'}
              </h1>
              <p className="text-sm text-muted-foreground">
                {language === 'fr' ? 'Créer et gérer les défis quotidiens' : language === 'pt' ? 'Criar e gerir desafios diários' : 'Create and manage daily challenges'}
              </p>
            </div>
          </div>
          <Button onClick={() => setShowCreateModal(true)} data-testid="create-challenge-btn">
            <Plus className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Nouveau défi' : language === 'pt' ? 'Novo desafio' : 'New challenge'}
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-6">
        {/* Default Templates */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {language === 'fr' ? 'Défis par défaut' : language === 'pt' ? 'Desafios padrão' : 'Default Challenges'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {templates.default_templates.map((template, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                      <Target className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {language === 'fr' ? template.title_fr : template.title_en}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {language === 'fr' ? template.description_fr : template.description_en}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-sm font-medium">
                      +{template.xp_reward} XP
                    </span>
                    <span className="text-xs text-muted-foreground px-2 py-1 bg-muted rounded">
                      {template.type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Custom Templates */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {language === 'fr' ? 'Défis personnalisés' : language === 'pt' ? 'Desafios personalizados' : 'Custom Challenges'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {templates.custom_templates.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{language === 'fr' ? 'Aucun défi personnalisé' : language === 'pt' ? 'Sem desafios personalizados' : 'No custom challenges'}</p>
                <p className="text-sm">{language === 'fr' ? 'Cliquez sur "Nouveau défi" pour en créer un' : language === 'pt' ? 'Clique em "Novo desafio" para criar um' : 'Click "New challenge" to create one'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {templates.custom_templates.map((template) => (
                  <div 
                    key={template.id}
                    className={`flex items-center justify-between p-4 rounded-lg border ${template.active ? 'bg-background' : 'bg-muted/30 opacity-60'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Zap className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {language === 'fr' ? template.title_fr : (language === 'pt' ? template.title_pt : template.title_en)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {language === 'fr' ? template.description_fr : (language === 'pt' ? template.description_pt : template.description_en)}
                        </p>
                        <div className="flex gap-1 mt-1">
                          {(template.languages || ['fr', 'en', 'pt']).map(lang => (
                            <span key={lang} className="text-xs px-1.5 py-0.5 bg-muted rounded uppercase">
                              {lang}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="px-2 py-1 bg-primary/10 text-primary rounded text-sm font-medium">
                        +{template.xp_reward} XP
                      </span>
                      <Switch
                        checked={template.active}
                        onCheckedChange={() => handleToggleActive(template)}
                      />
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openEditModal(template)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(template.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || !!editingTemplate} onOpenChange={(open) => {
        if (!open) {
          setShowCreateModal(false);
          setEditingTemplate(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              {editingTemplate 
                ? (language === 'fr' ? 'Modifier le défi' : language === 'pt' ? 'Editar desafio' : 'Edit challenge')
                : (language === 'fr' ? 'Nouveau défi' : language === 'pt' ? 'Novo desafio' : 'New challenge')
              }
            </DialogTitle>
          </DialogHeader>
          
          {/* AI Generation Section */}
          {!editingTemplate && (
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 p-4 rounded-lg border mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-purple-500" />
                <span className="font-medium">{language === 'fr' ? 'Génération IA' : language === 'pt' ? 'Geração IA' : 'AI Generation'}</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Input
                  value={aiTheme}
                  onChange={(e) => setAiTheme(e.target.value)}
                  placeholder={language === 'fr' ? 'Thème (optionnel)' : language === 'pt' ? 'Tema (opcional)' : 'Theme (optional)'}
                />
                <Select value={aiDifficulty} onValueChange={setAiDifficulty}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">{language === 'fr' ? 'Facile' : language === 'pt' ? 'Fácil' : 'Easy'}</SelectItem>
                    <SelectItem value="medium">{language === 'fr' ? 'Moyen' : language === 'pt' ? 'Médio' : 'Medium'}</SelectItem>
                    <SelectItem value="hard">{language === 'fr' ? 'Difficile' : language === 'pt' ? 'Difícil' : 'Hard'}</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  onClick={handleGenerateAI} 
                  disabled={generatingAI}
                  className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600"
                >
                  {generatingAI ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  {language === 'fr' ? 'Générer' : language === 'pt' ? 'Gerar' : 'Generate'}
                </Button>
              </div>
            </div>
          )}
          
          <div className="space-y-4 py-4">
            {/* Titles */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium mb-2">Titre (FR)</label>
                <Input
                  value={formData.title_fr}
                  onChange={(e) => setFormData({ ...formData, title_fr: e.target.value })}
                  placeholder="Titre en français"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Title (EN)</label>
                <Input
                  value={formData.title_en}
                  onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                  placeholder="Title in English"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Título (PT)</label>
                <Input
                  value={formData.title_pt}
                  onChange={(e) => setFormData({ ...formData, title_pt: e.target.value })}
                  placeholder="Título em português"
                />
              </div>
            </div>
            
            {/* Descriptions */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium mb-2">Description (FR)</label>
                <Textarea
                  value={formData.description_fr}
                  onChange={(e) => setFormData({ ...formData, description_fr: e.target.value })}
                  placeholder="Description en français"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description (EN)</label>
                <Textarea
                  value={formData.description_en}
                  onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                  placeholder="Description in English"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Descrição (PT)</label>
                <Textarea
                  value={formData.description_pt}
                  onChange={(e) => setFormData({ ...formData, description_pt: e.target.value })}
                  placeholder="Descrição em português"
                  rows={2}
                />
              </div>
            </div>
            
            {/* Languages target */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'fr' ? 'Langues cibles' : language === 'pt' ? 'Idiomas alvo' : 'Target languages'}
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={formData.languages.includes('fr')}
                    onCheckedChange={() => toggleLanguage('fr')}
                  />
                  <span>Français</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={formData.languages.includes('en')}
                    onCheckedChange={() => toggleLanguage('en')}
                  />
                  <span>English</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={formData.languages.includes('pt')}
                    onCheckedChange={() => toggleLanguage('pt')}
                  />
                  <span>Português</span>
                </label>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  {language === 'fr' ? 'Récompense XP' : language === 'pt' ? 'Recompensa XP' : 'XP Reward'}
                </label>
                <Input
                  type="number"
                  value={formData.xp_reward}
                  onChange={(e) => setFormData({ ...formData, xp_reward: e.target.value })}
                  min={1}
                  max={100}
                />
              </div>
              {!editingTemplate && (
                <div>
                  <label className="block text-sm font-medium mb-2">Type</label>
                  <Select
                    value={formData.challenge_type}
                    onValueChange={(value) => setFormData({ ...formData, challenge_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {challengeTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {['track_count', 'spend_limit', 'different_categories'].includes(formData.challenge_type) && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  {language === 'fr' ? 'Objectif (nombre)' : language === 'pt' ? 'Objetivo (número)' : 'Target (number)'}
                </label>
                <Input
                  type="number"
                  value={formData.target}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                  placeholder={formData.challenge_type === 'spend_limit' ? 'ex: 50 (€)' : 'ex: 3'}
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateModal(false);
              setEditingTemplate(null);
              resetForm();
            }}>
              {language === 'fr' ? 'Annuler' : language === 'pt' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={editingTemplate ? handleUpdate : handleCreate}>
              {editingTemplate 
                ? (language === 'fr' ? 'Mettre à jour' : language === 'pt' ? 'Atualizar' : 'Update')
                : (language === 'fr' ? 'Créer' : language === 'pt' ? 'Criar' : 'Create')
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
